import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, ArrowUpDown, List, Filter } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  TopPerformersCard, AlertsCard,
} from '../../../components/LevelDashboard';
import Pagination from '../../../../../shared/components/Pagination';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import {
  useGetClassHolisticAnalyticsQuery,
  getFoundationClassByIdFromRecords,
  getStudentsForClassFromRecords,
  deriveRecordMetrics,
  getClassDisplayName,
  parseTermFilter,
  termFilterToQueryValue,
  mergeAnalyticsRecordSets,
  getFoundationCompetencyLevel,
  FOUNDATION_BEGINNER_PCT,
} from '../../../services/holisticApi';
import { FoundationKPIHero } from '../components/FoundationKPIHero';
import { FoundationMasterTabs } from '../components/FoundationMasterTabs';


type SortKey = 'name' | 'competency' | 'behavior' | 'peer';
type StudentLevelFilter = 'Proficient' | 'Progressing' | 'Beginner';

const LEVEL_SLUG_TO_FILTER: Record<string, StudentLevelFilter> = {
  proficient:  'Proficient',
  progressing: 'Progressing',
  beginner:    'Beginner',
};

const levelToSlug = (level: StudentLevelFilter): string => level.toLowerCase();


const ClassDrillDown: React.FC = () => {
  const { schoolId, classId } = useParams<{ schoolId: string; classId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  const term = parseTermFilter(searchParams.get('term'));
  const isOverall = term === 'overall';
  const termValue = termFilterToQueryValue(term);
  const levelFilter = LEVEL_SLUG_TO_FILTER[(searchParams.get('studentLevel') || '').toLowerCase()] || undefined;
  const querySuffix = `?year=${academicYear}&term=${termValue}${levelFilter ? `&studentLevel=${levelToSlug(levelFilter)}` : ''}`;

  const buildClassQuery = (nextLevel?: StudentLevelFilter): string => {
    const params = new URLSearchParams();
    params.set('year', String(academicYear));
    params.set('term', termValue);
    if (nextLevel) {
      params.set('studentLevel', levelToSlug(nextLevel));
    }
    return `?${params.toString()}`;
  };

  const buildClassQueryWithTerm = (nextTermValue: string, nextLevel?: StudentLevelFilter): string => {
    const params = new URLSearchParams();
    params.set('year', String(academicYear));
    params.set('term', nextTermValue);
    if (nextLevel) {
      params.set('studentLevel', levelToSlug(nextLevel));
    }
    return `?${params.toString()}`;
  };

  const numericClassId = Number(classId);
  const numericSchoolId = Number(schoolId) || undefined;

  const singleTermQuery = useGetClassHolisticAnalyticsQuery(
    !isOverall ? { classId: numericClassId, academicYear, term: term as 1 | 2, schoolId: numericSchoolId } : skipToken,
  );
  const term1ClassQuery = useGetClassHolisticAnalyticsQuery(
    isOverall ? { classId: numericClassId, academicYear, term: 1, schoolId: numericSchoolId } : skipToken,
  );
  const term2ClassQuery = useGetClassHolisticAnalyticsQuery(
    isOverall ? { classId: numericClassId, academicYear, term: 2, schoolId: numericSchoolId } : skipToken,
  );

  const isLoading = isOverall
    ? (term1ClassQuery.isLoading || term2ClassQuery.isLoading)
    : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!isOverall) return singleTermQuery.data?.data || [];
    return mergeAnalyticsRecordSets(
      term1ClassQuery.data?.data || [],
      term2ClassQuery.data?.data || [],
    );
  }, [isOverall, singleTermQuery.data, term1ClassQuery.data, term2ClassQuery.data]);

  const cls = useMemo(
    () => getFoundationClassByIdFromRecords(records, schoolId!, classId!, term),
    [records, schoolId, classId, term],
  );

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('competency');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'All'>(10);

  // Use API records as the class-level student list.
  // For "Overall" term: average term1 + term2 competency from the embedded JSON
  // strings — same parseCompetencyValue logic used in StudentProfile.
  // For a specific term: use deriveRecordMetrics() on the domain scores.
  const allStudents = useMemo(() => {
    return getStudentsForClassFromRecords(records, schoolId!, classId!).map((record) => {
      const metrics = deriveRecordMetrics(record, term);
      const competency = metrics.competency;

      const first = (record.firstName ?? record.first_name ?? '').trim();
      const middle = (record.middleName ?? record.middle_name ?? '').trim();
      const last = (record.lastName ?? record.last_name ?? '').trim();
      const fullName = [first, middle, last].filter(Boolean).join(' ');
      return {
        id: record.student_id,
        name: fullName || `Student ${record.student_id}`,
        classLabel: getClassDisplayName(Number(record.class_id)),
        section: '-',
        competency,
        behavior: +metrics.behavior.toFixed(2),
        peer: +metrics.peer.toFixed(2),
        needsSupport: competency < FOUNDATION_BEGINNER_PCT,
        competencyLevel: getFoundationCompetencyLevel(competency),
        schoolName: record.schoolName || '',
      };
    }) as any[];
  }, [classId, isOverall, records, schoolId]);


  const levelCounts = useMemo(() => {
    return {
      Proficient:  allStudents.filter((s) => s.competencyLevel === 'Proficient').length,
      Progressing: allStudents.filter((s) => s.competencyLevel === 'Progressing').length,
      Beginner:    allStudents.filter((s) => s.competencyLevel === 'Beginner').length,
    };
  }, [allStudents]);

  const students = useMemo(() => {
    let list = [...allStudents];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q));
    }
    if (levelFilter) {
      list = list.filter(s => s.competencyLevel === levelFilter);
    }
    list.sort((a, b) => {
      let va: number, vb: number;
      switch (sortKey) {
        case 'name': return sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case 'competency': va = a.competency; vb = b.competency; break;
        case 'behavior': va = a.behavior; vb = b.behavior; break;
        case 'peer': va = a.peer; vb = b.peer; break;
        default: va = 0; vb = 0;
      }
      return sortAsc ? va - vb : vb - va;
    });
    return list;
  }, [allStudents, search, sortKey, sortAsc, levelFilter]);

  const effectivePageSize = pageSize === 'All' ? students.length : Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(students.length / (effectivePageSize || 10)));
  const pageStudents = students.slice((page - 1) * effectivePageSize, page * effectivePageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center p-6">
        <KVSLoader loading size={72} />
      </div>
    );
  }

  if (!cls) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Class not found.</p>
        <button onClick={() => navigate('..')} className="text-indigo-600 text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-indigo-600 select-none"
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sortKey === k ? 'text-indigo-600' : 'text-gray-300'} />
      </span>
    </th>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('..')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{cls.className} {cls.section}</h1>
          <p className="text-sm text-gray-500">Teacher: {cls.teacherName} &mdash; {cls.metrics.totalStudents} students</p>
        </div>
        <div className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
          <select
            id="term"
            value={termValue}
            onChange={(e) => navigate(`${location.pathname}${buildClassQueryWithTerm(e.target.value, levelFilter)}`)}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="overall">Overall</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
          </select>
        </div>
      </div>

      <FoundationKPIHero
        records={records}
        term={term}
        contextLabel={`${cls.className} ${cls.section} · ${cls.metrics.totalStudents.toLocaleString()} students`}
      />

      <FoundationMasterTabs records={records} term={term} classId={Number(classId)} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersCard items={cls.topPerformers} />
        <AlertsCard
          items={cls.alerts}
          onViewAll={() => navigate(`${location.pathname}${buildClassQuery('Beginner')}`)}
          viewAllLabel="Need Attention Students"
        />
      </div>

      {/* Student List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-700">Students</h3>
            <button
              onClick={() => navigate(`${location.pathname}${buildClassQuery()}`)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition"
            >
              <List size={14} /> See All Students
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search student..."
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 w-56"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          {(['Proficient', 'Progressing', 'Beginner'] as StudentLevelFilter[]).map((lvl) => {
            const isActive = levelFilter === lvl;
            return (
              <button
                key={lvl}
                onClick={() => navigate(`${location.pathname}${buildClassQuery(isActive ? undefined : lvl)}`)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${isActive
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
              >
                {lvl} ({levelCounts[lvl]})
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <SortHeader label="Name" k="name" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">School Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Class</th>
                <SortHeader label="Competency" k="competency" />
                <SortHeader label="Behavior" k="behavior" />
                <SortHeader label="Peer" k="peer" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {pageStudents.map(s => {
                const comp = s.competency;
                return (
                  <tr
                    key={s.id}
                    className="border-b border-gray-100 hover:bg-indigo-50/40 cursor-pointer transition"
                    onClick={() => navigate(`students/${s.id}/overview${querySuffix}`)}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{s.name}</p>
                      <p className="text-[11px] text-gray-400">{s.id}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-medium">{s.schoolName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{s.classLabel} {s.section}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-semibold ${comp >= 80 ? 'text-emerald-600' : comp >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{comp}%</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-semibold ${s.behavior >= 75 ? 'text-emerald-600' : s.behavior >= 55 ? 'text-amber-600' : 'text-red-600'}`}>{s.behavior}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-semibold ${s.peer >= 75 ? 'text-emerald-600' : s.peer >= 55 ? 'text-amber-600' : 'text-red-600'}`}>{s.peer}</span>
                    </td>
                    <td className="px-4 py-3">
                      {s.competencyLevel === 'Proficient' ? (
                        <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Proficient</span>
                      ) : s.competencyLevel === 'Progressing' ? (
                        <span className="text-xs font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">Progressing</span>
                      ) : (
                        <span className="text-xs font-semibold bg-red-50 text-red-700 px-2 py-0.5 rounded-full">Beginner</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {pageStudents.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No students found for this class.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Page Size Control */}
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <select
              value={typeof pageSize === 'number' && [10, 30, 50, 100].includes(pageSize) ? pageSize.toString() : (pageSize === 'All' ? 'All' : 'custom')}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'All') {
                  setPageSize('All');
                } else if (val === 'custom') {
                  setPageSize(10);
                } else {
                  setPageSize(Number(val));
                }
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 font-medium text-gray-700 cursor-pointer"
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="All">All</option>
              <option value="custom">Custom</option>
            </select>

            {pageSize !== 'All' && (
              <input
                type="number"
                min={1}
                max={students.length}
                value={pageSize}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val > 0) {
                    setPageSize(val);
                    setPage(1);
                  }
                }}
                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 w-16 text-center font-medium text-gray-700"
                placeholder="Num..."
              />
            )}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalRecords={students.length}
              pageSize={effectivePageSize}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassDrillDown;
